import { useState, useRef, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { getProductPrice } from '../../utils/pricing';

export default function OrderForm() {
    const { products, clients, addOrder } = useAppContext();

    // Cliente e entrega
    const [selectedClient, setSelectedClient] = useState('');
    const [deliveryDate, setDeliveryDate] = useState('');
    const [deliveryTime, setDeliveryTime] = useState('');
    const [deliveryNotes, setDeliveryNotes] = useState('');

    // Produto sendo adicionado
    const [selectedProduct, setSelectedProduct] = useState('');
    const [packageType, setPackageType] = useState('unit');
    const [quantity, setQuantity] = useState('');
    const [preparation, setPreparation] = useState('frito');
    const [useCustomPrice, setUseCustomPrice] = useState(false);
    const [customPrice, setCustomPrice] = useState('');

    // Carrinho de produtos
    const [cartItems, setCartItems] = useState([]);

    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    const clientRef = useRef(null);

    useEffect(() => {
        if (clientRef.current) clientRef.current.focus();
    }, []);

    // Calcular preço do produto baseado no tipo de embalagem
    const getItemPrice = (product, packageType) => {
        const unitPrice = getProductPrice(product);

        if (packageType === 'package') {
            return unitPrice * (product.packageSize || 10);
        } else if (packageType === 'cento') {
            return product.centoPrice || unitPrice * 100;
        }

        return unitPrice;
    };

    // Calcular quantidade real baseado no tipo de embalagem
    const getActualQuantity = (product, packageType, qty) => {
        if (packageType === 'package') {
            return qty * (product.packageSize || 10);
        } else if (packageType === 'cento') {
            return qty * 100;
        }
        return qty;
    };

    // Adicionar produto ao carrinho
    const handleAddToCart = (e) => {
        e.preventDefault();

        if (!selectedProduct || !quantity) return;

        const product = products.find(p => p.id === selectedProduct);
        if (!product) return;

        const qty = Number(quantity);
        const calculatedPrice = getItemPrice(product, packageType);
        const pricePerUnit = useCustomPrice && customPrice ? Number(customPrice) : calculatedPrice;
        const actualQuantity = getActualQuantity(product, packageType, qty);

        const cartItem = {
            id: Date.now().toString(),
            productId: product.id,
            productName: product.name,
            packageType,
            quantity: qty,
            actualQuantity,
            pricePerUnit,
            calculatedPrice,
            hasCustomPrice: useCustomPrice && customPrice ? true : false,
            totalPrice: pricePerUnit * qty,
            preparation: product.type === 'artesanal' ? preparation : null,
        };

        setCartItems([...cartItems, cartItem]);
        setSelectedProduct('');
        setQuantity('');
        setPackageType('unit');
        setPreparation('frito');
        setUseCustomPrice(false);
        setCustomPrice('');
    };

    // Remover item do carrinho
    const handleRemoveFromCart = (itemId) => {
        setCartItems(cartItems.filter(item => item.id !== itemId));
    };

    // Calcular total do pedido
    const orderTotal = cartItems.reduce((sum, item) => sum + item.totalPrice, 0);

    // Finalizar pedido
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!selectedClient) {
            setError('Selecione um cliente');
            return;
        }

        if (cartItems.length === 0) {
            setError('Adicione pelo menos um produto ao pedido');
            return;
        }

        if (!deliveryDate) {
            setError('Informe a data de entrega');
            return;
        }

        try {
            setSaving(true);

            const client = clients.find(c => c.id === selectedClient);

            const orderData = {
                clientId: selectedClient,
                clientName: client.name,
                clientPhone: client.phone || '',
                clientAddress: client.address || '',
                clientNeighborhood: client.neighborhood || '',
                clientCity: client.city || '',
                clientReference: client.reference || '',
                deliveryDate,
                deliveryTime,
                deliveryNotes,
                items: cartItems,
                totalAmount: orderTotal,
                status: 'pendente',
            };

            const result = await addOrder(orderData);

            setSuccess(`✓ Pedido ${result.orderNumber} criado com sucesso!`);

            // Limpar formulário
            setSelectedClient('');
            setDeliveryDate('');
            setDeliveryTime('');
            setDeliveryNotes('');
            setCartItems([]);
            setSelectedProduct('');
            setQuantity('');

            if (clientRef.current) clientRef.current.focus();

            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Não foi possível criar o pedido. Tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    const getPackageTypeLabel = (packageType) => {
        if (packageType === 'package') return 'Bandeja';
        if (packageType === 'cento') return 'Cento';
        return 'Unidade';
    };

    return (
        <form onSubmit={handleSubmit} className="card shadow-sm">
            <div className="card-body">
                <h2 className="h5 mb-3"><i className="bi bi-clipboard-plus me-2"></i>Novo Pedido</h2>

                {success && <div className="alert alert-success py-2 small">{success}</div>}
                {error && <div className="alert alert-danger py-2 small">{error}</div>}

                {/* Seleção de Cliente */}
                <div className="mb-3">
                    <label className="form-label fw-semibold">Cliente *</label>
                    <select
                        ref={clientRef}
                        className="form-select"
                        value={selectedClient}
                        onChange={(e) => setSelectedClient(e.target.value)}
                        required
                    >
                        <option value="">Selecione um cliente</option>
                        {clients.map((client) => (
                            <option key={client.id} value={client.id}>
                                {client.name} {client.phone ? `- ${client.phone}` : ''}
                            </option>
                        ))}
                    </select>
                    {clients.length === 0 && (
                        <div className="form-text small text-warning">
                            Cadastre clientes na aba Clientes primeiro
                        </div>
                    )}
                </div>

                {/* Endereço do cliente (preview) */}
                {selectedClient && (
                    <div className="mb-3 p-2 bg-light rounded border small">
                        {(() => {
                            const client = clients.find(c => c.id === selectedClient);
                            return (
                                <>
                                    <div className="fw-semibold"><i className="bi bi-geo-alt me-1"></i>Endereço de entrega:</div>
                                    <div>{client.address || 'Não informado'}</div>
                                    {client.neighborhood && <div>{client.neighborhood}</div>}
                                    {client.city && <div>{client.city}</div>}
                                    {client.reference && <div className="text-muted">Ref: {client.reference}</div>}
                                </>
                            );
                        })()}
                    </div>
                )}

                {/* Data e hora de entrega */}
                <div className="row">
                    <div className="col-7 mb-3">
                        <label className="form-label fw-semibold">Data de entrega *</label>
                        <input
                            type="date"
                            className="form-control"
                            value={deliveryDate}
                            onChange={(e) => setDeliveryDate(e.target.value)}
                            required
                        />
                    </div>
                    <div className="col-5 mb-3">
                        <label className="form-label fw-semibold">Horário</label>
                        <input
                            type="time"
                            className="form-control"
                            value={deliveryTime}
                            onChange={(e) => setDeliveryTime(e.target.value)}
                        />
                    </div>
                </div>

                {/* Observações de entrega */}
                <div className="mb-3">
                    <label className="form-label fw-semibold">Observações de entrega</label>
                    <textarea
                        className="form-control form-control-sm"
                        rows="2"
                        value={deliveryNotes}
                        onChange={(e) => setDeliveryNotes(e.target.value)}
                        placeholder="Ex: Ligar ao chegar, portão azul..."
                    />
                </div>

                <hr />

                {/* Adicionar produtos */}
                <h3 className="h6 mb-3"><i className="bi bi-cart-plus me-2"></i>Adicionar produtos</h3>

                <div className="mb-2">
                    <label className="form-label fw-semibold small">Produto</label>
                    <select
                        className="form-select form-select-sm"
                        value={selectedProduct}
                        onChange={(e) => {
                            setSelectedProduct(e.target.value);
                            setPackageType('unit');
                        }}
                    >
                        <option value="">Selecione um produto</option>
                        {products.map((product) => (
                            <option key={product.id} value={product.id}>
                                {product.name} - R$ {getProductPrice(product).toFixed(2)}
                            </option>
                        ))}
                    </select>
                </div>

                {selectedProduct && (() => {
                    const product = products.find(p => p.id === selectedProduct);
                    return (
                        <>
                            <div className="mb-2">
                                <label className="form-label fw-semibold small">Tipo de venda</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={packageType}
                                    onChange={(e) => setPackageType(e.target.value)}
                                >
                                    <option value="unit">Unidade - R$ {getProductPrice(product).toFixed(2)}</option>
                                    {product.sellByPackage && (
                                        <option value="package">
                                            Bandeja ({product.packageSize} un.) - R$ {(getProductPrice(product) * product.packageSize).toFixed(2)}
                                        </option>
                                    )}
                                    {product.sellByCento && (
                                        <option value="cento">
                                            Cento (100 un.) - R$ {product.centoPrice.toFixed(2)}
                                        </option>
                                    )}
                                </select>
                            </div>

                            <div className="mb-2">
                                <label className="form-label fw-semibold small">Quantidade</label>
                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    className="form-control form-control-sm"
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    placeholder="Qtd."
                                />
                            </div>

                            {/* Modo de preparo para produtos artesanais */}
                            {product.type === 'artesanal' && (
                                <div className="mb-2">
                                    <label className="form-label fw-semibold small">Modo de preparo</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={preparation}
                                        onChange={(e) => setPreparation(e.target.value)}
                                    >
                                        <option value="frito">Frito</option>
                                        <option value="assado">Assado</option>
                                        <option value="congelado">Congelado</option>
                                    </select>
                                </div>
                            )}

                            {/* Ajuste de preço */}
                            <div className="mb-2">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="useCustomPrice"
                                        checked={useCustomPrice}
                                        onChange={(e) => {
                                            setUseCustomPrice(e.target.checked);
                                            if (e.target.checked) {
                                                setCustomPrice(getItemPrice(product, packageType).toFixed(2));
                                            } else {
                                                setCustomPrice('');
                                            }
                                        }}
                                    />
                                    <label className="form-check-label small" htmlFor="useCustomPrice">
                                        Ajustar preço (adicional/desconto)
                                    </label>
                                </div>
                            </div>

                            {useCustomPrice && (
                                <div className="mb-2 p-2 bg-light rounded border">
                                    <label className="form-label fw-semibold small">Preço customizado</label>
                                    <div className="input-group input-group-sm">
                                        <span className="input-group-text">R$</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            className="form-control"
                                            value={customPrice}
                                            onChange={(e) => setCustomPrice(e.target.value)}
                                            placeholder="0,00"
                                        />
                                    </div>
                                    <div className="form-text small">
                                        Preço padrão: R$ {getItemPrice(product, packageType).toFixed(2)}
                                        {customPrice && (
                                            <>
                                                {Number(customPrice) > getItemPrice(product, packageType) ? (
                                                    <span className="text-success ms-2">
                                                        +R$ {(Number(customPrice) - getItemPrice(product, packageType)).toFixed(2)}
                                                    </span>
                                                ) : Number(customPrice) < getItemPrice(product, packageType) ? (
                                                    <span className="text-warning ms-2">
                                                        -R$ {(getItemPrice(product, packageType) - Number(customPrice)).toFixed(2)}
                                                    </span>
                                                ) : null}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            <button
                                type="button"
                                className="btn btn-sm btn-success w-100 mb-3"
                                onClick={handleAddToCart}
                                disabled={!quantity}
                            >
                                + Adicionar ao pedido
                            </button>
                        </>
                    );
                })()}

                {/* Carrinho */}
                {cartItems.length > 0 && (
                    <div className="mb-3">
                        <div className="fw-semibold small mb-2">Itens do pedido:</div>
                        <div className="list-group list-group-flush border rounded">
                            {cartItems.map((item) => (
                                <div key={item.id} className="list-group-item p-2 d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1 small">
                                        <div className="fw-semibold">
                                            {item.productName}
                                            {item.hasCustomPrice && (
                                                <span className="badge bg-warning text-dark ms-1" title="Preço customizado">
                                                    <i className="bi bi-pencil-fill"></i>
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-muted">
                                            {item.quantity} {getPackageTypeLabel(item.packageType)}
                                            {item.packageType !== 'unit' && ` (${item.actualQuantity} un.)`}
                                            {item.preparation && (
                                                <span className="ms-1">
                                                    • {item.preparation === 'frito' ? 'Frito' :
                                                       item.preparation === 'assado' ? 'Assado' :
                                                       'Congelado'}
                                                </span>
                                            )}
                                        </div>
                                        {item.hasCustomPrice && (
                                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                Preço ajustado: R$ {item.pricePerUnit.toFixed(2)}
                                                {item.calculatedPrice && (
                                                    <span className="ms-1">
                                                        (padrão: R$ {item.calculatedPrice.toFixed(2)})
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-end">
                                        <div className="fw-bold small">R$ {item.totalPrice.toFixed(2)}</div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-link text-danger p-0"
                                            onClick={() => handleRemoveFromCart(item.id)}
                                            title="Remover"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-2 p-2 bg-success bg-opacity-10 rounded border border-success">
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="fw-semibold">Total do pedido:</span>
                                <span className="fw-bold fs-5 text-success">R$ {orderTotal.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                )}

                <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={saving || cartItems.length === 0}
                >
                    {saving ? <><i className="bi bi-hourglass-split me-2"></i>Criando pedido...</> : <><i className="bi bi-check-circle me-2"></i>Finalizar pedido</>}
                </button>
            </div>
        </form>
    );
}
